import { Injectable } from '@angular/core';
import JSZip from 'jszip';
import { fileNameNormalizer } from '@app/shared/misc/fileNameNormalizer';
import {
  FileSystemDirectoryEntryLike,
  FileSystemEntryLike,
  FileSystemFileEntryLike,
  FileSystemTreeEntry,
  FileTreeNode,
} from './file-browser.models';

const ZIP_FILE_NAME = 'upload-pick-data.zip';
const ZIP_MIME_TYPE = 'application/x-www-form-urlencoded';

@Injectable({ providedIn: 'root' })
export class FileBrowserUploadBuilderService {
  async createZipUploadFileFromPickerFiles(files: File[]): Promise<File> {
    const zip = new JSZip();
    this.addFilesToZip(zip, files, '');
    const blob = await zip.generateAsync({ type: 'blob' });
    return this.createUploadFile(blob);
  }

  async createZipUploadFileFromEntries(entries: FileSystemTreeEntry[]): Promise<File> {
    const zip = new JSZip();
    const tree = await this.buildTree(entries, '');

    this.addFilesToZip(zip, tree.files, '');
    this.addDirectoriesToZip(zip, tree.directories, '');

    const blob = await zip.generateAsync({ type: 'blob' });
    return this.createUploadFile(blob);
  }

  private createUploadFile(blob: Blob): File {
    return new File([blob], ZIP_FILE_NAME, { type: ZIP_MIME_TYPE });
  }

  private addFilesToZip(zip: JSZip, files: File[], path: string): void {
    files.forEach((file) => {
      if (file.name === '.DS_Store') {
        return;
      }

      const fullPath = this.createNormalizedPath(path, file.name);
      zip.file(fullPath, file, { createFolders: true });
    });
  }

  private addDirectoriesToZip(zip: JSZip, directories: FileTreeNode[], path: string): void {
    directories.forEach((directory) => {
      const fullPath = this.createNormalizedPath(path, directory.name);
      zip.folder(fullPath);

      if (directory.files.length > 0) {
        this.addFilesToZip(zip, directory.files, fullPath);
      }

      if (directory.directories.length > 0) {
        this.addDirectoriesToZip(zip, directory.directories, fullPath);
      }
    });
  }

  private createNormalizedPath(path: string, name: string): string {
    return fileNameNormalizer(fileNameNormalizer(`${path}/${name}`));
  }

  private async buildTree(entries: FileSystemEntryLike[], name: string): Promise<FileTreeNode> {
    const tree: FileTreeNode = {
      name,
      files: [],
      directories: [],
    };

    const promises = entries.map((entry) => {
      if (entry.isFile) {
        return this.parseFileEntry(entry as FileSystemFileEntryLike).then((file) => {
          tree.files.push(file);
        });
      }

      if (entry.isDirectory) {
        return this.parseDirectoryEntry(entry as FileSystemDirectoryEntryLike).then((directory) => {
          tree.directories.push(directory);
        });
      }

      return Promise.resolve();
    });

    await Promise.all(promises);
    return tree;
  }

  private parseFileEntry(fileEntry: FileSystemFileEntryLike): Promise<File> {
    return new Promise<File>((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });
  }

  private parseDirectoryEntry(directoryEntry: FileSystemDirectoryEntryLike): Promise<FileTreeNode> {
    return new Promise<FileTreeNode>((resolve, reject) => {
      const directoryReader = directoryEntry.createReader();
      directoryReader.readEntries(
        (entries) => {
          this.buildTree(entries, directoryEntry.name).then(resolve).catch(reject);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }
}
